/**
 * The night's standings — who is ahead, by the one ranking rule.
 *
 * STANDINGS ONLY. Fixtures and the bracket live in FixturesTab; this tab
 * aggregates, that one lists. Nothing here needs play order — the endpoint
 * returns rows already sorted, and re-sorting them in the client would put a
 * fourth copy of the ranking rule in the codebase.
 *
 * WHAT IT SHOWS DEPENDS ON THE STAGE, AND THE SERVER DECIDES. Through the group
 * stage a per-group table IS the competition. Once a bracket exists it is not:
 * half the field is out and the rest are no longer playing anyone from their
 * own group, so the tab switches wholesale to one combined ranking of the whole
 * field across both stages. `hasKnockout` is the switch and `combined` is null
 * until it flips — neither is inferred here.
 *
 * The group tables are NOT kept below the combined one. Two tables ranking the
 * same players on different scopes, one of them stale, is how an admin ends up
 * reading the wrong number off the wrong table.
 */

import { formatAvg } from '../../lib/format.js';

/**
 * One ranked table, shared by both views.
 *
 * The position column is the row's index, not a rank the server sent. That
 * matches the season table, and it means two genuinely level players read as
 * consecutive positions rather than a shared one — compareStandings always
 * breaks the tie on name, so the order is at least stable between reloads.
 *
 * `showGroup` names the group a player qualified out of. It earns its place
 * only on the combined table; on a group table every row would repeat the
 * number already in the heading.
 */
function StandingsTable({ rows, showGroup = false }) {
  return (
    <div className="hd-table-wrap">
      <table className="hd-table hd-admin-table">
        <thead>
          <tr>
            <th style={{ width: 36 }}>#</th>
            <th>Player</th>
            {showGroup && <th className="hd-table-num">Grp</th>}
            <th className="hd-table-num">P</th>
            <th className="hd-table-num">W</th>
            <th className="hd-table-num">L</th>
            <th className="hd-table-num">Legs</th>
            <th className="hd-table-num">+/−</th>
            <th className="hd-table-num">Avg</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.entry_id}>
              <td className="hd-muted">{i + 1}</td>
              <td>{row.display_name}</td>
              {showGroup && (
                <td className="hd-table-num">
                  {row.group_number ?? <span className="hd-muted">—</span>}
                </td>
              )}
              <td className="hd-table-num">{row.matches_played}</td>
              <td className="hd-table-num">{row.matches_won}</td>
              <td className="hd-table-num">{row.matches_lost}</td>
              <td className="hd-table-num">{row.legs_won}–{row.legs_lost}</td>
              <td className="hd-table-num">
                {row.leg_difference > 0 ? `+${row.leg_difference}` : row.leg_difference}
              </td>
              <td className="hd-table-num">{formatAvg(row.average)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * A group's table, with the board it was played on.
 *
 * Carried over unchanged from the Standings tab FixturesTab replaced, and it
 * still takes the same `group` prop. The heading was always the only thing
 * that made this a GROUP table; the table beneath it now serves the combined
 * view too, which is why that part moved into StandingsTable rather than being
 * copied.
 */
function GroupTable({ group }) {
  return (
    <section>
      <h3 className="hd-section-title">
        Group {group.groupNumber}
        {group.boardNumber != null && (
          <span className="hd-meta"> · board {group.boardNumber}</span>
        )}
      </h3>

      <StandingsTable rows={group.standings} />
    </section>
  );
}

/* ------------------------------------------------------------------------- */

export default function StandingsTab({ groups, combined, hasKnockout }) {
  if (hasKnockout) {
    // A bracket with nobody in it is not a state the generator can produce, so
    // this is a guard rather than a case — but an empty table with headings
    // reads as a bug, and this does not.
    if (!combined?.length) {
      return <div className="hd-empty">No standings yet — nobody has played.</div>;
    }

    return (
      <div className="hd-stack">
        <section>
          <h3 className="hd-section-title">Combined standings</h3>
          <p className="hd-meta">
            Group and knockout matches together, for everyone who was drawn.
            A player knocked out keeps their place here.
          </p>

          <StandingsTable rows={combined} showGroup />
        </section>
      </div>
    );
  }

  if (!groups.length) {
    return <div className="hd-empty">No standings yet — nothing has been drawn.</div>;
  }

  return (
    <div className="hd-stack">
      {groups.map((group) => (
        <GroupTable key={group.groupNumber} group={group} />
      ))}
    </div>
  );
}
